import React, { useState } from 'react';
import { CheckCircle, X, Star, ArrowRight, Shield, Zap, Users, Clock, XCircle, Infinity, Crown } from 'lucide-react';

const PricingPage: React.FC = () => {
  const [selectedPackage, setSelectedPackage] = useState<'diy' | 'done-for-you'>('done-for-you');

  const diyFeatures = [
    'Complete source code and system',
    'YOU customize and rebrand it yourself',
    'Remove our branding, add YOUR company name',
    'Sell to unlimited clients - keep 100% profits',
    'Full source code and customization documentation',
    'Basic setup guide and tutorials',
    'Email support for 30 days',
    'All features and modules included',
    'Deployment instructions',
    'No ongoing fees or restrictions'
  ];

  const doneForYouFeatures = [
    'Everything in DIY Package',
    'WE customize it for your chosen niche',
    'WE add your branding and design',
    'Niche-specific terminology and features',
    'Industry-optimized workflows',
    'Custom domain setup',
    'Fast-track customization service',
    '90 days premium support',
    'Training session included',
    'Priority customization queue'
  ];

  const niches = [
    'Healthcare/Medical practices',
    'Fitness & Wellness centers', 
    'Beauty & Spa services',
    'Legal & Professional services',
    'Consulting & Business services',
    'Education & Training',
    'Home services',
    'Or any custom niche you specify'
  ];

  const comparisonFeatures = [
    {
      feature: 'Complete Source Code',
      diy: true,
      doneForYou: true
    },
    {
      feature: 'White-Label Rights',
      diy: true,
      doneForYou: true
    },
    {
      feature: 'All Core Features',
      diy: true,
      doneForYou: true
    },
    {
      feature: 'Source Code Access',
      diy: true,
      doneForYou: true
    },
    {
      feature: 'Custom Niche Setup',
      diy: false,
      doneForYou: true
    },
    {
      feature: 'Custom Branding',
      diy: false,
      doneForYou: true
    },
    {
      feature: 'Industry-Specific Features',
      diy: false,
      doneForYou: true
    },
    {
      feature: 'Domain Setup',
      diy: false,
      doneForYou: true
    },
    {
      feature: 'Fast-Track Service',
      diy: false,
      doneForYou: true
    },
    {
      feature: 'Training Session',
      diy: false,
      doneForYou: true
    },
    {
      feature: 'Support Duration',
      diy: '30 days',
      doneForYou: '90 days'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-innovation text-white relative overflow-hidden">
      {/* Global Animated Background Elements */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 to-transparent"></div>
      <div className="absolute top-20 left-10 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute bottom-20 right-10 w-[500px] h-[500px] bg-cyan-500/10 rounded-full blur-3xl animate-pulse" style={{animationDelay: '2s'}}></div>
      <div className="absolute top-1/2 left-1/4 w-64 h-64 bg-purple-500/5 rounded-full blur-2xl animate-pulse" style={{animationDelay: '4s'}}></div>
      <div className="absolute bottom-1/3 right-1/3 w-80 h-80 bg-blue-500/8 rounded-full blur-3xl animate-pulse" style={{animationDelay: '6s'}}></div>
      
      {/* Hero Section */}
      <section className="py-20 relative z-10 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-white via-blue-100 to-cyan-300 bg-clip-text text-transparent">
            Own Your Platform,<br />Build Your Success
          </h1>
          <p className="text-xl md:text-2xl text-slate-300 max-w-4xl mx-auto mb-8 leading-relaxed">
            Stop paying monthly fees forever! Get a <span className="text-cyan-300 font-semibold">$50K+ enterprise platform</span> for a one-time investment. 
            <span className="text-yellow-300 font-semibold"> Choose your path to business freedom</span> - DIY or fully done-for-you.
          </p>
          <div className="mb-8 flex flex-col sm:flex-row gap-4 justify-center">
            <div className="bg-gradient-to-r from-red-500/20 to-pink-500/20 backdrop-blur-xl border border-red-300/30 text-white px-8 py-4 rounded-2xl text-lg font-bold shadow-2xl hover:shadow-red-500/25 transition-all duration-300 hover:scale-105">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-red-400 rounded-full animate-pulse"></div>
                <XCircle className="h-5 w-5 text-red-400" />
                No Monthly Fees Ever
              </div>
            </div>
            <div className="bg-gradient-to-r from-green-500/20 to-emerald-400/20 backdrop-blur-xl border border-green-300/30 text-white px-8 py-4 rounded-2xl text-lg font-bold shadow-2xl hover:shadow-green-500/25 transition-all duration-300 hover:scale-105">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                <Infinity className="h-5 w-5 text-green-400" />
                Unlimited Scale & Growth
              </div>
            </div>
            <div className="bg-gradient-to-r from-purple-500/20 to-blue-500/20 backdrop-blur-xl border border-purple-300/30 text-white px-8 py-4 rounded-2xl text-lg font-bold shadow-2xl hover:shadow-purple-500/25 transition-all duration-300 hover:scale-105">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-purple-400 rounded-full animate-pulse"></div>
                <Crown className="h-5 w-5 text-purple-400" />
                100% Complete Ownership
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Pricing Cards */}
      <section className="py-20 relative z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8">
            {/* DIY Package */}
            <div className={`bg-slate-800/80 backdrop-blur-sm rounded-2xl shadow-xl p-8 relative border border-slate-700/50 hover:border-cyan-500/50 transition-all duration-300 ${selectedPackage === 'diy' ? 'ring-2 ring-cyan-500' : ''}`}>
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-white mb-2">DIY White-Label Package</h3>
                <div className="text-5xl font-bold text-cyan-400 mb-2">$799</div>
                <p className="text-slate-300 mb-4">One-time payment</p>
                <p className="text-sm text-slate-400 italic">
                  Perfect for developers and tech-savvy entrepreneurs
                </p>
              </div>

              <div className="mb-8">
                <h4 className="font-semibold text-white mb-4">What's Included:</h4>
                <ul className="space-y-3">
                  {diyFeatures.map((feature, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <CheckCircle className="h-5 w-5 text-green-400 mt-0.5 flex-shrink-0" />
                      <span className="text-slate-300">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-blue-50 p-4 rounded-lg mb-6">
                <h4 className="font-semibold text-blue-900 mb-2">Key Benefits:</h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• Get a $50K+ platform for under $1,000</li>
                  <li>• Complete ownership - no monthly fees ever</li>
                  <li>• Customize it exactly how you want</li>
                  <li>• Full source code and documentation</li>
                </ul>
              </div>

              <button
                onClick={() => setSelectedPackage('diy')}
                className="w-full bg-gradient-to-r from-cyan-600 to-blue-600 text-white px-6 py-4 rounded-lg hover:from-cyan-500 hover:to-blue-500 transition-all duration-200 font-semibold text-lg mb-4 shadow-lg hover:shadow-cyan-500/25 transform hover:scale-105"
              >
                Choose DIY Package
              </button>
              
              <p className="text-center text-sm text-slate-400">
                Instant access to full platform
              </p>
            </div>

            {/* Done-For-You Package */}
            <div className={`bg-slate-800/80 backdrop-blur-sm rounded-2xl shadow-xl p-8 relative border-2 border-cyan-500/50 hover:border-cyan-400/70 transition-all duration-300 ${selectedPackage === 'done-for-you' ? 'ring-2 ring-cyan-600' : ''}`}>
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-6 py-2 rounded-full text-sm font-bold shadow-lg">
                  MOST POPULAR
                </span>
              </div>

              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-white mb-2">Done-For-You White-Label</h3>
                <div className="text-5xl font-bold text-cyan-400 mb-2">$1,299</div>
                <p className="text-slate-300 mb-4">One-time payment</p>
                <p className="text-sm text-slate-400 italic">
                  Perfect for non-technical business owners
                </p>
              </div>

              <div className="mb-8">
                <h4 className="font-semibold text-white mb-4">What's Included:</h4>
                <ul className="space-y-3">
                  {doneForYouFeatures.map((feature, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <CheckCircle className="h-5 w-5 text-green-400 mt-0.5 flex-shrink-0" />
                      <span className={`text-slate-300 ${index === 1 ? 'font-semibold text-cyan-300' : ''}`}>
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-primary-50 p-4 rounded-lg mb-6">
                <h4 className="font-semibold text-primary-900 mb-2">Key Benefits:</h4>
                <ul className="text-sm text-primary-800 space-y-1">
                  <li>• We customize everything for your specific niche</li>
                  <li>• Fast-track customization with your branding</li>
                  <li>• Industry-specific features and terminology</li>
                  <li>• Includes training and premium support</li>
                </ul>
              </div>

              <button
                onClick={() => setSelectedPackage('done-for-you')}
                className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-6 py-4 rounded-lg hover:from-cyan-400 hover:to-blue-400 transition-all duration-200 font-semibold text-lg mb-4 shadow-lg hover:shadow-cyan-500/25 transform hover:scale-105"
              >
                Choose Done-For-You
              </button>
              
              <p className="text-center text-sm text-slate-400">
                Instant access to full platform
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Supported Niches */}
      <section className="py-20 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              We Customize For Any Industry
            </h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              With our Done-For-You package, we'll tailor the platform specifically for your industry
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {niches.map((niche, index) => (
              <div key={index} className="bg-slate-800/60 backdrop-blur-sm rounded-lg p-6 text-center hover:bg-slate-700/60 transition-all duration-200 group">
                <div className="w-12 h-12 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-200">
                  <Star className="h-6 w-6 text-cyan-400" />
                </div>
                <h3 className="font-semibold text-white mb-2">{niche}</h3>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <p className="text-slate-300 mb-4">
              Don't see your industry? We can customize for any business model.
            </p>
            <a href="/industries" className="text-cyan-400 hover:text-cyan-300 font-semibold inline-flex items-center transition-colors duration-200">
              See All Industries
              <ArrowRight className="ml-2 h-4 w-4" />
            </a>
          </div>
        </div>
      </section>

      {/* Feature Comparison */}
      <section className="py-20 relative z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Package Comparison
            </h2>
            <p className="text-xl text-slate-300">
              See exactly what's included in each package
            </p>
          </div>

          <div className="bg-slate-800/80 backdrop-blur-sm rounded-2xl shadow-lg overflow-hidden border border-slate-700/50">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-cyan-600 to-blue-600 text-white">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Feature</th>
                    <th className="px-6 py-4 text-center text-sm font-semibold">DIY Package<br />$799</th>
                    <th className="px-6 py-4 text-center text-sm font-semibold">Done-For-You<br />$1,299</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700/50">
                  {comparisonFeatures.map((item, index) => (
                    <tr key={index} className="hover:bg-slate-700/30 transition-colors duration-200">
                      <td className="px-6 py-4 text-sm font-medium text-white">{item.feature}</td>
                      <td className="px-6 py-4 text-center">
                        {typeof item.diy === 'boolean' ? (
                          item.diy ? (
                            <CheckCircle className="h-5 w-5 text-green-400 mx-auto" />
                          ) : (
                            <X className="h-5 w-5 text-red-400 mx-auto" />
                          )
                        ) : (
                          <span className="text-sm text-slate-300">{item.diy}</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {typeof item.doneForYou === 'boolean' ? (
                          item.doneForYou ? (
                            <CheckCircle className="h-5 w-5 text-green-400 mx-auto" />
                          ) : (
                            <X className="h-5 w-5 text-red-400 mx-auto" />
                          )
                        ) : (
                          <span className="text-sm text-slate-300">{item.doneForYou}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="py-20 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Why This Is An Incredible Deal
            </h2>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-green-600">$50K+</span>
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Development Investment</h3>
              <p className="text-slate-300">
                Get years of development work for a fraction of the cost. This platform represents over $50K in development investment.
              </p>
            </div>
            
            <div className="text-center">
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Proven Platform</h3>
              <p className="text-slate-300">
                Battle-tested in real business environments. You're getting a proven, reliable solution.
              </p>
            </div>
            
            <div className="text-center">
              <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="h-8 w-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Complete Ownership</h3>
              <p className="text-slate-300">
                No ongoing fees, no restrictions. You own the platform completely and can sell unlimited licenses.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 relative z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Frequently Asked Questions
            </h2>
          </div>

          <div className="space-y-8">
            <div className="bg-slate-800/80 rounded-lg p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-white mb-2">
                What's the difference between the two packages?
              </h3>
              <p className="text-slate-300">
                The DIY package gives you everything to customize yourself, while Done-For-You includes our team customizing it specifically for your industry with fast-track priority service.
              </p>
            </div>

            <div className="bg-slate-800/80 rounded-lg p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-white mb-2">
                Are there any ongoing fees?
              </h3>
              <p className="text-slate-300">
                No! This is a one-time purchase with complete ownership. No monthly fees, no restrictions, no limitations on how you use or sell the platform.
              </p>
            </div>

            <div className="bg-slate-800/80 rounded-lg p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-white mb-2">
                Can I sell this to my clients?
              </h3>
              <p className="text-slate-300">
                Absolutely! First, you customize it for your niche (or we do it for you with Done-For-You), then you have full commercial rights to sell it to unlimited clients under YOUR brand at whatever price you choose.
              </p>
            </div>

            <div className="bg-slate-800/80 rounded-lg p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-white mb-2">
                What if I need a custom industry setup?
              </h3>
              <p className="text-slate-300">
                Choose our Done-For-You package and we'll customize it for any industry or business model you specify, including custom terminology and workflows.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 relative z-10 text-white">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Own Your Platform?
          </h2>
          <p className="text-xl mb-8 text-blue-100">
            Join smart entrepreneurs who chose to own instead of rent their business platform
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-yellow-400 text-primary-900 px-8 py-4 rounded-lg text-lg font-bold hover:bg-yellow-300 transition-colors duration-200">
              Get Started Now
            </button>
            <a
              href="/demo"
              className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-bold hover:bg-slate-800/80 hover:text-primary-600 transition-colors duration-200"
            >
              Try Demo First
            </a>
          </div>
          <p className="text-sm text-blue-200 mt-4">
            Instant access • Complete ownership • No questions asked
          </p>
        </div>
      </section>
    </div>
  );
};

export default PricingPage;
